import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Home, Mail, Phone, MapPin, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import Since1998Badge from "@/components/Since1998Badge";

const SMSTerms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>SMS Terms & Conditions | Its-Sold.com</title>
        <meta name="description" content="SMS messaging terms and conditions for Its-Sold.com real estate notifications in Dallas-Fort Worth." />
        <link rel="canonical" href="https://its-sold.com/sms-terms" />
      </Helmet>
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                Its-Sold.com<sup className="text-xs">™</sup>
              </span>
            </div>
            <Since1998Badge variant="header" />
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">SMS Terms & Conditions</h1>
          <p className="text-gray-500 mb-8">Last Updated: February 2026</p>

          <div className="prose max-w-none space-y-8">
            
            {/* Business Information - Top for verification */}
            <section className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Program Sponsor Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <p className="flex items-center gap-2">
                    <strong>Legal Business Name:</strong> Net Auctions, Inc.
                  </p>
                  <p className="flex items-center gap-2">
                    <strong>DBA:</strong> Its-Sold.com
                  </p>
                  <p className="flex items-center gap-2">
                    <strong>EIN:</strong> 41-3894967
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-500" />
                    <a href="https://its-sold.com" className="text-blue-600 hover:underline">https://its-sold.com</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <a href="mailto:support@its-sold.com" className="text-blue-600 hover:underline">support@its-sold.com</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <a href="tel:+18173205334" className="text-blue-600 hover:underline">(817) 320-5334</a>
                  </p>
                  <p className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                    <span>3709 South Shadycreek Drive, Arlington, TX 76013</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Separate Consent Programs */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">SMS Programs & Consent</h2>
              <p className="text-gray-700 mb-4">
                Its-Sold.com operates <strong>two separate SMS programs</strong>. Each program requires 
                its own explicit opt-in consent. You may opt-in to either, both, or neither program.
              </p>

              {/* Program 1: Transactional */}
              <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-5 mb-4">
                <h3 className="text-lg font-bold text-orange-900 mb-3">
                  Program 1: Transactional Alerts
                </h3>
                <div className="text-gray-700 space-y-2 text-sm">
                  <p><strong>Purpose:</strong> Account-related notifications and service alerts</p>
                  <p><strong>Message Types:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Lead assignment notifications (for realtors)</li>
                    <li>Listing status confirmations</li>
                    <li>Account security alerts</li>
                    <li>Listing expiration reminders</li>
                  </ul>
                  <p><strong>Frequency:</strong> 1-3 messages per month</p>
                  <p><strong>Opt-In Method:</strong> Checkbox during account registration labeled "Transactional Alerts"</p>
                </div>
              </div>

              {/* Program 2: Promotional */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-5 mb-4">
                <h3 className="text-lg font-bold text-blue-900 mb-3">
                  Program 2: Promotional Messages
                </h3>
                <div className="text-gray-700 space-y-2 text-sm">
                  <p><strong>Purpose:</strong> Marketing communications and special offers</p>
                  <p><strong>Message Types:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Special offers and discounts</li>
                    <li>New feature announcements</li>
                    <li>Platform tips and updates</li>
                  </ul>
                  <p><strong>Frequency:</strong> 1-3 messages per month</p>
                  <p><strong>Opt-In Method:</strong> Separate checkbox during account registration labeled "Promotional Messages"</p>
                </div>
              </div>

              <div className="bg-gray-100 rounded-lg p-4 text-sm">
                <p className="text-gray-700">
                  <strong>Important:</strong> Each SMS program requires separate consent. Opting into one program 
                  does not automatically opt you into the other. Message and data rates may apply to all programs.
                </p>
              </div>
            </section>

            {/* Opt-In Process */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Opt-In Process</h2>
              <div className="space-y-3 text-gray-700">
                <p>To receive SMS messages from Its-Sold.com, you must:</p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>Visit <a href="https://its-sold.com/register" className="text-blue-600 hover:underline">its-sold.com/register</a></li>
                  <li>Enter your phone number in the registration form</li>
                  <li>Check the specific checkbox(es) for the SMS program(s) you wish to join:
                    <ul className="list-disc list-inside ml-6 mt-1">
                      <li>"Transactional Alerts" - for account notifications</li>
                      <li>"Promotional Messages" - for offers and updates</li>
                    </ul>
                  </li>
                  <li>Complete your account registration</li>
                </ol>
                <p className="mt-3 text-sm bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <strong>Note:</strong> SMS consent is NOT required to create an account or use our services. 
                  You can use Its-Sold.com without opting into any SMS program.
                </p>
              </div>
            </section>

            {/* Opt-Out Instructions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Opt-Out</h2>
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                <p className="font-semibold text-red-900 mb-2">To Stop Receiving SMS Messages:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  <li>Reply <strong>STOP</strong> to any message from us</li>
                  <li>Reply <strong>STOP ALL</strong> to opt-out of all programs</li>
                  <li>Email <a href="mailto:support@its-sold.com" className="text-blue-600 hover:underline">support@its-sold.com</a> with your phone number</li>
                  <li>Call <a href="tel:+18173205334" className="text-blue-600 hover:underline">(817) 320-5334</a></li>
                </ul>
              </div>
              <p className="text-gray-700 text-sm">
                After opting out, you will receive a one-time confirmation message. To re-subscribe, 
                reply <strong>START</strong> or update your preferences in your account settings.
              </p>
            </section>

            {/* Help */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Help & Support</h2>
              <div className="text-gray-700">
                <p className="mb-3">For assistance with our SMS programs:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Reply <strong>HELP</strong> to any message for immediate assistance</li>
                  <li>Email: <a href="mailto:support@its-sold.com" className="text-blue-600 hover:underline">support@its-sold.com</a></li>
                  <li>Phone: <a href="tel:+18173205334" className="text-blue-600 hover:underline">(817) 320-5334</a></li>
                </ul>
              </div>
            </section>

            {/* Privacy - No Third Party Sharing */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Privacy & Data Protection</h2>
              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-5 mb-4">
                <p className="font-bold text-green-900 text-lg mb-2">YOUR PHONE NUMBER IS NEVER SHARED</p>
                <p className="text-gray-700">
                  Its-Sold.com will <strong>NEVER</strong> share, sell, rent, or disclose your phone number 
                  to any third party, including:
                </p>
                <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                  <li>Realtors or real estate agents</li>
                  <li>Marketing partners or advertisers</li>
                  <li>Data brokers or aggregators</li>
                  <li>Any other third-party service providers</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  <strong>All SMS messages are sent directly by Its-Sold.com (Net Auctions, Inc.)</strong> - 
                  never by third parties on our behalf.
                </p>
              </div>
            </section>

            {/* Message & Data Rates */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Message & Data Rates</h2>
              <p className="text-gray-700 mb-3">
                Standard message and data rates from your wireless carrier may apply to messages 
                sent and received. Contact your carrier for details about your messaging plan.
              </p>
              <p className="text-gray-700">
                <strong>Supported Carriers:</strong> AT&T, Verizon, T-Mobile, Sprint, and most major U.S. carriers.
              </p>
            </section>

            {/* Disclaimers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitations</h2>
              <p className="text-gray-700 mb-2">Its-Sold.com is not responsible for:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Delayed or undelivered messages due to carrier network issues</li>
                <li>Charges from your mobile carrier for receiving messages</li>
                <li>Messages sent to incorrect numbers due to user input error</li>
                <li>Service interruptions beyond our control</li>
              </ul>
            </section>

            {/* Contact */}
            <section className="border-t pt-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="font-semibold text-gray-900 mb-3">Net Auctions, Inc. (DBA Its-Sold.com)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <div>
                    <p><strong>Address:</strong></p>
                    <p>3709 South Shadycreek Drive</p>
                    <p>Arlington, TX 76013</p>
                  </div>
                  <div>
                    <p><strong>Phone:</strong> (817) 320-5334</p>
                    <p><strong>Email:</strong> support@its-sold.com</p>
                    <p><strong>Website:</strong> https://its-sold.com</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t">
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Homepage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSTerms;
